"use client"

import type { HouseholdMember, SearchResult } from "@/schema/search"
import {
  ArrowDownLeft,
  ArrowUpRight,
  Building2,
  Calendar,
  DollarSign,
  Hash,
  MessageSquare,
  Phone,
  Shield,
  TrendingUp,
  User,
  Users,
  WandSparkles,
} from "lucide-react"

import { BookIcon } from "@/components/books/book-icon"
import { PhoneBadgeWithRingout } from "@/components/tables/phone-badge-with-ringout"

import { PreviewAddress, PreviewContactFields, PreviewField, PreviewTags, PreviewWebsite } from "./preview-fields"
import { RecentMeetings } from "./recent-meetings"

function HouseholdMembers({ members = [], householdId }: { members?: HouseholdMember[]; householdId: string }) {
  const leaders = members.filter((member) => ["Head of Household", "Spouse", "Partner"].includes(member.role ?? ""))
  const additionalMembers = members.length - leaders.length
  return (
    <div className="flex flex-col gap-1.5 pb-0.5">
      {leaders.length === 0 && <PreviewField icon={User} label="Household leaders" />}
      {leaders.map((member, idx) => (
        <div key={idx}>
          <PreviewField icon={User} label="Household member">
            <span className="font-medium">{member.name}</span>
            {member.role && <span className="text-muted-foreground"> · {member.role}</span>}
          </PreviewField>
          <div className="pl-[18px]">
            <PreviewContactFields
              email={member.email}
              phone={member.phone}
              extension={member.phone_extension}
              name={member.name}
              householdId={householdId}
              showEmpty={false}
            />
          </div>
        </div>
      ))}
      {additionalMembers > 0 && (
        <p className="text-muted-foreground -mt-1 pb-1.5 pl-[18px] text-xs">
          +{additionalMembers} additional {additionalMembers === 1 ? "member" : "members"}
        </p>
      )}
    </div>
  )
}

function formatDate(value?: string) {
  return value ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null
}

function formatCurrency(value?: number) {
  return value == null
    ? null
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
}

function PreviewDetails({ item, onNavigate }: { item: SearchResult; onNavigate?: () => void }) {
  switch (item.type) {
    case "household":
    case "client":
    case "past_client":
    case "prospect": {
      const attrs = item.attributes
      return (
        <>
          {attrs.tier && (
            <PreviewField icon={Hash} label="Tier">
              {attrs.tier}
            </PreviewField>
          )}
          <HouseholdMembers members={attrs.members} householdId={item.id} />
          <PreviewAddress address={attrs} showEmpty={false} />
          <PreviewTags tags={attrs.tags} count={attrs.tag_count} />
          <PreviewField icon={WandSparkles} label="AI summary" wrap>
            {attrs.ai_summary}
          </PreviewField>
          <RecentMeetings type="household" id={item.id} bookId={item.book_id} onNavigate={onNavigate} />
        </>
      )
    }
    case "contact": {
      const attrs = item.attributes
      return (
        <>
          <PreviewContactFields
            email={attrs.email}
            phone={attrs.phone}
            extension={attrs.phone_extension}
            name={item.display_label}
          />
          <PreviewField icon={Building2} label="Company">
            {attrs.company}
          </PreviewField>
          <PreviewField icon={User} label="Job title">
            {attrs.job_title}
          </PreviewField>
          <PreviewAddress address={attrs} />
          <PreviewTags tags={attrs.tags} count={attrs.tag_count} />
          <RecentMeetings type="contact" id={item.id} bookId={item.book_id} onNavigate={onNavigate} />
        </>
      )
    }
    case "business": {
      const attrs = item.attributes
      return (
        <>
          <PreviewContactFields
            email={attrs.primary_email}
            phone={attrs.primary_phone}
            extension={attrs.phone_extension}
            name={item.display_label}
          />
          <PreviewWebsite url={attrs.website_url} />
          <PreviewField icon={Building2} label="Industry">
            {attrs.industry}
          </PreviewField>
          <PreviewAddress address={attrs} />
          <PreviewTags tags={attrs.tags} count={attrs.tag_count} />
          <PreviewField icon={MessageSquare} label="Additional information" wrap>
            {attrs.additional_info}
          </PreviewField>
          <RecentMeetings type="business" id={item.id} bookId={item.book_id} onNavigate={onNavigate} />
        </>
      )
    }
    case "trust": {
      const attrs = item.attributes
      return (
        <>
          <PreviewField icon={Shield} label="Household">
            {attrs.household_name}
          </PreviewField>
          <PreviewContactFields
            email={attrs.primary_email}
            phone={attrs.primary_phone}
            extension={attrs.phone_extension}
            name={attrs.household_name ?? item.display_label}
          />
          <PreviewField icon={Hash} label="Tax ID">
            {attrs.tax_id}
          </PreviewField>
          <PreviewField icon={MessageSquare} label="Additional information" wrap>
            {attrs.additional_info}
          </PreviewField>
        </>
      )
    }
    case "opportunity": {
      const attrs = item.attributes
      return (
        <>
          <PreviewField icon={DollarSign} label="Value">
            {formatCurrency(attrs.value)}
          </PreviewField>
          <PreviewField icon={TrendingUp} label="Stage">
            {attrs.stage}
          </PreviewField>
          <PreviewField icon={Hash} label="Pipeline">
            {attrs.pipeline}
          </PreviewField>
          <PreviewField icon={TrendingUp} label="Probability">
            {attrs.probability == null ? null : `${attrs.probability}%`}
          </PreviewField>
          <PreviewField icon={Users} label="Relationship">
            {attrs.opportunityable || attrs.household_name}
          </PreviewField>
          <PreviewField icon={User} label="Owner">
            {attrs.owner}
          </PreviewField>
          <PreviewField icon={Calendar} label="Expected close">
            {formatDate(attrs.expected_close_date)}
          </PreviewField>
        </>
      )
    }
    case "user":
      return (
        <PreviewContactFields email={item.attributes.email} phone={item.attributes.phone} name={item.display_label} />
      )
    case "person": {
      const attrs = item.attributes
      return (
        <>
          <PreviewContactFields email={attrs.email} phone={attrs.phone} name={item.display_label} />
          <PreviewField icon={Building2} label="Company">
            {attrs.company}
          </PreviewField>
          <PreviewField icon={User} label="Job title">
            {attrs.job_title}
          </PreviewField>
          <PreviewField icon={Users} label="Household">
            {attrs.household_name}
          </PreviewField>
          <PreviewField icon={Hash} label="Role">
            {attrs.household_role}
          </PreviewField>
          <PreviewTags tags={attrs.tags} />
        </>
      )
    }
    case "sms_message": {
      const attrs = item.attributes
      const incoming = attrs.direction === "incoming"
      return (
        <>
          <PreviewField icon={MessageSquare} label="Message" wrap>
            {attrs.message_preview}
          </PreviewField>
          <PreviewField icon={incoming ? ArrowDownLeft : ArrowUpRight} label="Direction">
            {attrs.direction ? (incoming ? "Incoming" : "Outgoing") : null}
          </PreviewField>
          <PreviewField icon={User} label="Person">
            {attrs.person_name}
          </PreviewField>
          <PreviewField icon={Phone} label="Phone">
            {attrs.phone_number ? (
              <PhoneBadgeWithRingout value={attrs.phone_number} name={attrs.person_name ?? item.display_label} />
            ) : null}
          </PreviewField>
          <PreviewField icon={Calendar} label="Sent">
            {formatDate(attrs.sent_at)}
          </PreviewField>
        </>
      )
    }
    default:
      return null
  }
}

export function PreviewPane({ selectedItem, onNavigate }: { selectedItem?: SearchResult; onNavigate?: () => void }) {
  return (
    <div
      data-command-preview
      role="region"
      aria-label="Record preview"
      tabIndex={-1}
      className="bg-background [&_:is(a,button,[role=button]):not([data-button-badge]):is(:hover,:focus-visible)]:bg-accent [&_:is(a,button,[role=button]):not([data-button-badge]):is(:hover,:focus-visible)]:text-accent-foreground [&_[data-button-badge]:hover]:bg-blue-background [&_:is(a,button,[role=button]):focus-visible]:outline-ring [&_a:focus-visible>[data-button-badge]]:outline-ring max-h-[420px] w-96 shrink-0 overflow-y-auto border-l outline-none [&_:is(a,button,[role=button])]:rounded-md [&_:is(a,button,[role=button]):focus-visible]:ring-0 [&_:is(a,button,[role=button]):focus-visible]:ring-offset-0 [&_:is(a,button,[role=button]):focus-visible]:outline-2 [&_:is(a,button,[role=button]):focus-visible]:-outline-offset-2 [&_:is(a,button,[role=button]):focus-visible]:outline-solid [&_a:focus-visible>[data-button-badge]]:outline-2 [&_a:focus-visible>[data-button-badge]]:-outline-offset-2 [&_a:focus-visible>[data-button-badge]]:outline-solid"
    >
      {selectedItem ? (
        <div className="text-tertiary-foreground p-3">
          <div className="mb-1 flex items-start justify-between gap-2">
            <h3 className="text-foreground min-w-0 text-sm font-semibold">{selectedItem.display_label}</h3>
            {selectedItem.book_id && (
              <div title={selectedItem.book_name}>
                <BookIcon
                  bookId={selectedItem.book_id}
                  name={selectedItem.book_name}
                  className="bg-sidebar-accent text-sidebar-foreground size-8 rounded-lg"
                  textClassName="text-xs font-medium"
                />
              </div>
            )}
          </div>
          <PreviewDetails item={selectedItem} onNavigate={onNavigate} />
        </div>
      ) : (
        <div className="flex h-full items-center justify-center">
          <p className="text-muted-foreground text-xs">Select a record to see details</p>
        </div>
      )}
    </div>
  )
}
